import { z } from 'zod';
import {
  formatLimitAmount,
  isLimitUnchanged,
  type MarketLimitsFormValues,
  validateMaxOrderValue,
  validatePriceCollarPercentage,
  validateSegmentPositionCap,
} from '@/services/api/markets';

// A field left as the dialog opened it is always valid, because it is not sent: the exchange may
// hold a value this form would not accept (a decimal cap set directly on it, no collar at all).
const limitField = (initial: string, validate: (value: string) => string | null) =>
  z.string().superRefine((value, context) => {
    if (isLimitUnchanged(value, initial)) return;
    const message = validate(value);
    if (message) context.addIssue({ code: 'custom', message });
  });

/** Validation for one market's limit fields, relative to the values the form opened with. */
export const buildMarketLimitsFieldsSchema = (initial: MarketLimitsFormValues) =>
  z.object({
    priceCollarPercentage: limitField(initial.priceCollarPercentage, validatePriceCollarPercentage),
    maxOrderValue: limitField(initial.maxOrderValue, validateMaxOrderValue),
    RETAIL: limitField(initial.RETAIL, validateSegmentPositionCap),
    INSTITUTIONAL: limitField(initial.INSTITUTIONAL, validateSegmentPositionCap),
    MARKET_MAKER: limitField(initial.MARKET_MAKER, validateSegmentPositionCap),
  });

export const COMMENT_SCHEMA = z.string().trim().min(1, 'This field is required');

/** A numeric value is echoed back with thousands separators; anything else is left to the field's error. */
export const describeAmount = (value: string, quoteCode: string): string | null =>
  value !== '' && !Number.isNaN(Number(value)) ? `${formatLimitAmount(value)} ${quoteCode}` : null;
